import { useEffect, useRef } from 'react';
import { useNavTheme } from 'contexts/NavThemeContext';

// ==============================|| ANIMATED BACKGROUND ||============================== //

export default function AnimatedBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mousePosRef = useRef({ x: -1000, y: -1000 });
  const { mode } = useNavTheme();

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mousePosRef.current = { x: e.clientX, y: e.clientY };
    };
    const handleMouseLeave = () => {
      mousePosRef.current = { x: -1000, y: -1000 };
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    const gridColor = mode === 'dark' ? 'rgba(99, 102, 241, 0.015)' : 'rgba(79, 70, 229, 0.025)';
    const connectionColor = mode === 'dark' ? [129, 140, 248] : [79, 70, 229];
    const particleGlowColor = mode === 'dark' ? [129, 140, 248] : [99, 102, 241];
    const particleCoreColor = mode === 'dark' ? [165, 180, 252] : [79, 70, 229];

    interface Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      baseVx: number;
      baseVy: number;
      size: number;
      opacity: number;
      pulsePhase: number;
    }

    const particles: Particle[] = [];
    const numParticles = 35;
    const connectionDistance = 150;

    for (let i = 0; i < numParticles; i++) {
      const vx = (Math.random() - 0.5) * 0.15;
      const vy = (Math.random() - 0.5) * 0.15;
      particles.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        vx,
        vy,
        baseVx: vx,
        baseVy: vy,
        size: Math.random() * 1.5 + 0.8,
        opacity: Math.random() * 0.25 + 0.15,
        pulsePhase: Math.random() * Math.PI * 2,
      });
    }

    let animationId: number;
    let time = 0;

    const animate = () => {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      time += 0.005;

      ctx.strokeStyle = gridColor;
      ctx.lineWidth = 1;
      const gridSize = 80;

      for (let x = 0; x < window.innerWidth; x += gridSize) {
        ctx.beginPath();
        const offset = Math.sin(time * 0.5 + x * 0.0008) * 1.5;
        ctx.moveTo(x, offset);
        ctx.lineTo(x, window.innerHeight + offset);
        ctx.stroke();
      }

      for (let y = 0; y < window.innerHeight; y += gridSize) {
        ctx.beginPath();
        const offset = Math.cos(time * 0.5 + y * 0.0008) * 1.5;
        ctx.moveTo(offset, y);
        ctx.lineTo(window.innerWidth + offset, y);
        ctx.stroke();
      }

      const mousePos = mousePosRef.current;

      particles.forEach((particle) => {
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.pulsePhase += 0.01;

        const mouseDist = Math.hypot(particle.x - mousePos.x, particle.y - mousePos.y);
        if (mouseDist < 150 && mouseDist > 20) {
          const force = ((150 - mouseDist) / 150) * 0.001;
          const dx = particle.x - mousePos.x;
          const dy = particle.y - mousePos.y;
          const dist = Math.max(mouseDist, 1);
          particle.vx += (dx / dist) * force;
          particle.vy += (dy / dist) * force;
        }

        particle.vx += (particle.baseVx - particle.vx) * 0.015;
        particle.vy += (particle.baseVy - particle.vy) * 0.015;

        const maxVelocity = 0.4;
        const currentVelocity = Math.hypot(particle.vx, particle.vy);
        if (currentVelocity > maxVelocity) {
          particle.vx = (particle.vx / currentVelocity) * maxVelocity;
          particle.vy = (particle.vy / currentVelocity) * maxVelocity;
        }

        particle.vx *= 0.998;
        particle.vy *= 0.998;

        if (particle.x < -10) particle.x = window.innerWidth + 10;
        if (particle.x > window.innerWidth + 10) particle.x = -10;
        if (particle.y < -10) particle.y = window.innerHeight + 10;
        if (particle.y > window.innerHeight + 10) particle.y = -10;
      });

      particles.forEach((particle, i) => {
        for (let j = i + 1; j < particles.length; j++) {
          const other = particles[j];
          const distance = Math.hypot(particle.x - other.x, particle.y - other.y);
          if (distance < connectionDistance) {
            const opacity = (1 - distance / connectionDistance) * (mode === 'dark' ? 0.08 : 0.1);
            ctx.strokeStyle = `rgba(${connectionColor.join(',')}, ${opacity})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(other.x, other.y);
            ctx.stroke();
          }
        }
      });

      particles.forEach((particle) => {
        const pulse = Math.sin(particle.pulsePhase) * 0.15 + 0.85;

        ctx.beginPath();
        const gradient = ctx.createRadialGradient(particle.x, particle.y, 0, particle.x, particle.y, particle.size * 8);
        const baseOpacity = Math.floor(particle.opacity * pulse * (mode === 'dark' ? 180 : 220));
        gradient.addColorStop(0, `rgba(${particleGlowColor.join(',')}, ${baseOpacity / 255})`);
        gradient.addColorStop(0.4, `rgba(${particleGlowColor.join(',')}, 0.03)`);
        gradient.addColorStop(1, `rgba(${particleGlowColor.join(',')}, 0)`);
        ctx.fillStyle = gradient;
        ctx.arc(particle.x, particle.y, particle.size * 8, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `rgba(${particleCoreColor.join(',')}, ${particle.opacity * 0.9})`;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size * pulse, 0, Math.PI * 2);
        ctx.fill();
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, [mode]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
}
